import { useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type ApiResponse } from "@/api/client";
import type { AdminDestinationListItem, TripType, TripWithRelations } from "@ckgo2/shared/types";
import { TRIP_TYPE_LABELS, TRIP_STATUSES, TRIP_TYPES } from "@ckgo2/shared/types";
import { enumLabel } from "@/i18n/enumLabels";
import { useFacetedParams } from "@/hooks/useFacetedParams";
import { useAdminDestinationList } from "@/hooks/useAdminLists";
import FacetedListLayout from "@/components/common/FacetedListLayout";
import FacetPanel from "@/components/common/FacetPanel";
import ListSortSelect from "@/components/common/ListSortSelect";
import {
  countBuckets,
  matchesFacet,
  pageSlice,
  compareCzech,
  compareNewest,
} from "@/lib/clientFacets";
import {
  Plus,
  Plane,
  Edit,
  Trash2,
  Calendar,
  MapPin,
  MapPinOff,
  Copy,
  ListOrdered,
} from "lucide-react";
import { formatPrice, isHotelTrip } from "@ckgo2/shared/utils";
import { hotelCoordinates, sortBySearchMatch } from "@ckgo2/shared";

const PAGE_SIZE = 50;
const DEFAULT_SORT = "recent";

/**
 * Seznam zájezdů.
 *
 * Filtry, hledání i řazení ve URL (`useFacetedParams`) — týž vzor jako u poptávek a faktur.
 * Facet „Země" počítá zájezd k ZEMI, i když visí na lokalitě (Kjóto → Japonsko), takže
 * garant země vidí jedním kliknutím celý svůj katalog.
 *
 * Výchozí řazení je „naposledy upravené". API vrací seznam podle `createdAt`, což u pracovního
 * seznamu znamená nejstarší nahoře — nikdo tam nezačíná.
 */
export default function TripsPage() {
  const { t } = useTranslation();
  const { getValues, search, page, params } = useFacetedParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["trips"],
    queryFn: () => api.get<ApiResponse<TripWithRelations[]>>("/api/admin/trips"),
  });

  // Hierarchie destinací — pro roll-up lokality na zemi ve facetě.
  const { data: destData } = useAdminDestinationList();

  const destIndex = useMemo(() => {
    const byId = new Map<string, AdminDestinationListItem>();
    for (const d of destData?.data ?? []) byId.set(d.id, d);
    return byId;
  }, [destData]);

  /** Země zájezdu: nadřazená destinace lokality, jinak destinace sama. */
  const countryOf = useCallback(
    (trip: TripWithRelations) => {
      const id = trip.destinationId ?? trip.destination?.id;
      if (!id) return null;
      return destIndex.get(id)?.parentId || id;
    },
    [destIndex],
  );
  const countryName = (id: string) => destIndex.get(id)?.name ?? id;

  const duplicateMutation = useMutation({
    mutationFn: (tripId: string) =>
      api.post<ApiResponse<TripWithRelations>>(`/api/admin/trips/${tripId}/duplicate`),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      if (response.data?.id) {
        navigate(`/trips/${response.data.id}`);
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/trips/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
    },
  });

  const handleDelete = (id: string, title: string) => {
    if (confirm(t("trips.list.confirmDelete", { title }))) {
      deleteMutation.mutate(id);
    }
  };

  const trips = useMemo(() => data?.data ?? [], [data]);
  const sort = params.get("sort") ?? DEFAULT_SORT;
  const statusSel = getValues("status");
  const countrySel = getValues("country");
  const typeSel = getValues("tripType");

  const facets = useMemo(
    () => ({
      status: countBuckets(trips, (trip) => trip.status),
      country: countBuckets(trips, countryOf),
      tripType: countBuckets(trips, (trip) => trip.tripType),
    }),
    [trips, countryOf],
  );

  const filtered = useMemo(() => {
    const rows = trips.filter(
      (trip) =>
        matchesFacet(statusSel, trip.status) &&
        matchesFacet(countrySel, countryOf(trip)) &&
        matchesFacet(typeSel, trip.tripType),
    );
    return sortBySearchMatch(rows, search, {
      name: (trip) => trip.title,
      other: (trip) => [trip.slug, trip.destination?.name],
      tieBreak: (a, b) => {
        if (sort === "title") return compareCzech(a.title, b.title);
        if (sort === "price") return (a.priceFrom ?? 0) - (b.priceFrom ?? 0);
        if (sort === "order") {
          // Nezařazené (bez pořadí) až za žebříčkem, jinak by je nula vystřelila nahoru.
          const ao = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
          const bo = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
          return ao - bo || compareCzech(a.title, b.title);
        }
        return compareNewest(a.updatedAt ?? a.createdAt, b.updatedAt ?? b.createdAt);
      },
    });
  }, [trips, search, statusSel, countrySel, typeSel, sort, countryOf]);

  const visible = pageSlice(filtered, page, PAGE_SIZE);

  if (error) {
    return <div className="card text-error-600 text-center">{t("trips.list.loadError")}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("trips.list.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">{t("trips.list.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Objevitelnost z místa, kde operátor katalogu je (řez 08). */}
          <Link to="/trips/order" className="btn-secondary">
            <ListOrdered className="h-5 w-5" />
            {t("trips.order.openFromList")}
          </Link>
          <Link to="/trips/new" className="btn-primary">
            <Plus className="h-5 w-5" />
            {t("trips.list.newTrip")}
          </Link>
        </div>
      </div>

      <FacetedListLayout
        searchPlaceholder={t("trips.list.searchPlaceholder")}
        total={filtered.length}
        limit={PAGE_SIZE}
        isLoading={isLoading}
        isFetching={isFetching}
        actions={
          <ListSortSelect
            defaultValue={DEFAULT_SORT}
            options={[
              { value: "recent", label: t("shared.listSort.recent") },
              { value: "title", label: t("shared.listSort.titleAsc") },
              { value: "price", label: t("shared.listSort.priceAsc") },
              { value: "order", label: t("shared.listSort.orderAsc") },
            ]}
          />
        }
        filters={
          <>
            <FacetPanel
              title={t("common.fields.status")}
              paramKey="status"
              order={{ enum: TRIP_STATUSES }}
              buckets={facets.status}
              labelFor={(v) =>
                v === "published"
                  ? t("common.status.published")
                  : v === "draft"
                    ? t("common.status.draft")
                    : t("common.status.archived")
              }
            />
            <FacetPanel
              title={t("common.fields.destination")}
              paramKey="country"
              order="name"
              buckets={facets.country}
              labelFor={countryName}
              nullLabel={t("shared.listSort.withoutDestination")}
            />
            <FacetPanel
              title={t("common.fields.type")}
              paramKey="tripType"
              order={{ enum: TRIP_TYPES }}
              buckets={facets.tripType}
              labelFor={(v) => enumLabel(TRIP_TYPE_LABELS, v as TripType)}
            />
          </>
        }
      >
        <div className="card overflow-hidden p-0">
          {visible.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center text-gray-500">
              <Plane className="mb-2 h-12 w-12 text-gray-300" />
              <p>{t("trips.list.empty")}</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("common.fields.trip")}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("common.fields.destination")}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("common.fields.price")}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("trips.list.duration")}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("common.fields.status")}
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("common.fields.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {visible.map((trip) => (
                  <tr key={trip.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {trip.featuredImage ? (
                          <img
                            src={trip.featuredImage}
                            alt={trip.title}
                            className="h-10 w-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700">
                            <Plane className="h-5 w-5 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{trip.title}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">/{trip.slug}</p>
                          {/* Hotel bez polohy nemá na webu mapu (vize C13, F11.3). */}
                          {isHotelTrip(trip) && !hotelCoordinates(trip) && (
                            <span
                              data-chybi-souradnice
                              title={t("trips.list.noCoordinatesHint")}
                              className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
                            >
                              <MapPinOff className="h-3 w-3" aria-hidden="true" />
                              {t("trips.list.noCoordinates")}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600 dark:text-gray-300">
                          {trip.destination?.name || "-"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {trip.priceIsFrom
                          ? t("trips.list.priceFrom", { price: formatPrice(trip.priceFrom) })
                          : formatPrice(trip.priceFrom)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600 dark:text-gray-300">
                          {t("trips.list.days", { count: trip.durationDays ?? 0 })}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {trip.status === "published" ? (
                        <span className="badge bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400">
                          {t("common.status.published")}
                        </span>
                      ) : trip.status === "draft" ? (
                        <span className="badge bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400">
                          {t("common.status.draft")}
                        </span>
                      ) : (
                        <span className="badge bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          {t("common.status.archived")}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => duplicateMutation.mutate(trip.id)}
                          disabled={duplicateMutation.isPending}
                          className="btn-ghost btn-sm"
                          title={t("common.actions.duplicate")}
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <Link
                          to={`/trips/${trip.id}`}
                          className="btn-ghost btn-sm"
                          title={t("common.actions.edit")}
                        >
                          <Edit className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => handleDelete(trip.id, trip.title)}
                          disabled={deleteMutation.isPending}
                          className="btn-ghost btn-sm text-error-600 hover:bg-error-50 hover:text-error-700 dark:text-error-400 dark:hover:bg-error-900/20"
                          title={t("common.actions.delete")}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </FacetedListLayout>
    </div>
  );
}
